// Sign-up flow v2 — the shell. Replaces RegisterPage + OnboardingPage at
// /register and /onboarding with the approved v6 design: Account → Your
// business → Connect → Review → Portions → Done, with the plate summary and
// the Ask Mise assistant in the right rail (a bottom sheet on narrow widths).
//
// Real wiring throughout: POST /auth/register, the integrations' own OAuth and
// credential endpoints, PATCH /onboarding/restaurant, menu import, vendors,
// hire invites, settings.modules, POST /onboarding/complete. State persists to
// localStorage so OAuth round-trips resume where the owner left off.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './signup.css';
import { bizFromEmail, SHARES, TOOLS_BY, typeFromRestaurant, type ToolKey, type TypeKey } from './data';
import { clear, FRESH, load, pendingSignupAtConnect, save, type SignupState } from './state';
import {
  authed, connectFaireToken, connectToast, errCode, errMsg, fetchCurrentRestaurant, fetchMe,
  finishSetup, importFromWebsite, importMenuFile, registerAccount, runImport, saveBusinessEdits,
  sendInvites, startOAuth, submitReview, toolConnected, type LiveRow,
} from './wiring';
import { Assistant, TIPS, type Draft } from './assistant';
import { Account, Business, CallSheet, Connect, Done, mods, OAuthSheet, Portions, Review, Summary, TopBar } from './screens';

/** OAuth providers bounce back to /settings?tab=integrations&…=connected.
 * When a registered-but-unfinished signup is sitting in storage, steer that
 * landing back into the flow so the owner picks up exactly where they left
 * off. Mounted next to the router (see App.tsx). */
export function SignupResumeCatcher() {
  useEffect(() => {
    if (window.location.pathname !== '/settings') return;
    if (!/tab=integrations/.test(window.location.search)) return;
    if (pendingSignupAtConnect()) window.location.replace('/register');
  }, []);
  return null;
}

function initialState(): SignupState {
  const s = load();
  // Website links land here with ?type=cpg / ?type=bakery — honor it on a
  // fresh flow only.
  if (!s.type && !s.registered) {
    const q = new URLSearchParams(window.location.search).get('type');
    if (q === 'cpg') s.type = 'cpg';
    else if (q === 'bakery') s.type = 'bake';
  }
  return s;
}

export default function SignupV2Page() {
  const navigate = useNavigate();
  const [s, setS] = useState<SignupState>(initialState);
  const sRef = useRef(s);
  sRef.current = s;
  const get = useCallback(() => sRef.current, []);
  const set = useCallback((fn: (o: SignupState) => SignupState) => {
    const n = fn(sRef.current);
    sRef.current = n;
    setS(n);
  }, []);
  const up = useCallback((p: Partial<SignupState>) => set((o) => ({ ...o, ...p })), [set]);
  useEffect(() => { save(s); }, [s]);

  const go = useCallback((n: number) => set((o) => {
    const x = { ...o, step: Math.max(0, Math.min(5, n)) };
    if (x.step >= 1 && !x.biz) {
      const f = bizFromEmail(x.email);
      if (f) { x.biz = f.name; if (!x.site) x.site = f.domain; }
    }
    return x;
  }), [set]);

  // ---------- layout: narrow = assistant becomes a bottom sheet ----------
  const root = useRef<HTMLDivElement>(null);
  const sc = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState(false);
  const narrowRef = useRef(false);
  useEffect(() => {
    if (!root.current) return;
    const ro = new ResizeObserver(([e]) => { narrowRef.current = e.contentRect.width < 760; setNarrow(narrowRef.current); });
    ro.observe(root.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { if (sc.current) sc.current.scrollTop = 0; }, [s.step]);

  // ---------- assistant ----------
  const [draft, setDraft] = useState<Draft | null>(null);
  const [askOpen, setAskOpen] = useState(false);
  const ask = useCallback((text: string, prefill?: boolean) => {
    setDraft({ text, send: !prefill, n: Date.now() });
    if (narrowRef.current) setAskOpen(true);
  }, []);
  useEffect(() => {
    const cur = get();
    const tip = TIPS[cur.step];
    if (tip && !cur.chat.some((m) => m.tip === cur.step)) {
      up({ chat: [...cur.chat, { r: 'a', t: tip(cur), tip: cur.step }] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.step]);

  // ---------- boot / resume ----------
  const [booted, setBooted] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cur = get();
      if (!authed()) {
        // The saved flow claims an account but the session is gone — start
        // clean; signing in brings them back through /onboarding.
        if (cur.registered) { clear(); set(() => ({ ...FRESH })); }
        if (!cancelled) setBooted(true);
        return;
      }
      const me = await fetchMe();
      if (cancelled) return;
      if (me) {
        if (!cur.registered) {
          if (me.status === 'onboarding') {
            // An account mid-onboarding without a saved flow (older register,
            // another device) — adopt it into the flow at Connect.
            const full = await fetchCurrentRestaurant();
            if (cancelled) return;
            const settings = full?.settings && typeof full.settings === 'object' ? (full.settings as Record<string, unknown>) : {};
            const type = typeFromRestaurant(me.restaurantType, (settings as { businessType?: string }).businessType || null);
            set((o) => ({
              ...o,
              step: 2,
              registered: true,
              type,
              biz: me.restaurantName || o.biz,
              email: me.email || o.email,
              name: [me.firstName, me.lastName].filter(Boolean).join(' ') || o.name,
              locs: o.locs || '1',
            }));
          } else {
            // An active account browsing to /register — the dashboard is home.
            navigate('/', { replace: true });
            return;
          }
        } else {
          set((o) => ({ ...o, step: Math.max(o.step, 2), email: o.email || me.email || '', biz: o.biz || me.restaurantName || '' }));
        }
      }
      if (!cancelled) setBooted(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- connect: real status checks + import runs ----------
  const [liveRows, setLiveRows] = useState<Partial<Record<ToolKey, LiveRow[]>>>({});
  const startImport = useCallback((t: ToolKey, hint: string | null) => {
    up({ tools: { ...get().tools, [t]: 'importing' } });
    setLiveRows((r) => ({ ...r, [t]: SHARES[t].map((label) => ({ label, detail: null, state: 'wait' as const })) }));
    void runImport(t, hint, (rows) => setLiveRows((r) => ({ ...r, [t]: rows })))
      .then(({ rows, patch }) => {
        const cur = get();
        up({ ...patch(cur), tools: { ...cur.tools, [t]: 'done' }, imports: { ...cur.imports, [t]: rows } });
      })
      .catch(() => {
        // The connection stands even if the first sync hiccups.
        const cur = get();
        up({ tools: { ...cur.tools, [t]: 'done' }, imports: { ...cur.imports, [t]: SHARES[t].map((label) => ({ label, detail: 'Syncing in the background' })) } });
      })
      .finally(() => setLiveRows((r) => { const n = { ...r }; delete n[t]; return n; }));
  }, [get, up]);

  const checking = useRef(false);
  useEffect(() => {
    if (!booted || !s.registered || s.step !== 2 || !s.type || checking.current) return;
    checking.current = true;
    (async () => {
      for (const t of TOOLS_BY[get().type as TypeKey]) {
        const cur = get();
        if (cur.tools[t]) continue;
        const ok = await toolConnected(t);
        if (!ok) continue;
        if (cur.imports[t]) up({ tools: { ...get().tools, [t]: 'done' } });
        else startImport(t, null);
      }
      checking.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted, s.registered, s.step, s.type]);

  // ---------- business: register / save ----------
  const [bizBusy, setBizBusy] = useState(false);
  const [bizErr, setBizErr] = useState<string | null>(null);
  const siteKicked = useRef(false);
  const kickSiteImport = useCallback((site: string) => {
    if (!site.trim() || siteKicked.current) return;
    siteKicked.current = true;
    void importFromWebsite(site)
      .then(({ itemsCreated, details }) => {
        const cur = get();
        const pl = { ...cur.place };
        let touched = false;
        const take = (k: keyof typeof pl, v?: string | null) => { if (v && !pl[k]) { (pl as Record<string, unknown>)[k] = v; touched = true; } };
        take('street', details.addressStreet1);
        take('city', details.addressCity);
        take('state', details.addressState);
        take('zip', details.addressPostalCode);
        take('phone', details.phone);
        if (touched) pl.src = pl.src || 'your website';
        up({ place: pl, itemsImported: cur.itemsImported + (itemsCreated || 0) });
      })
      .catch(() => { /* unreadable site — the form still works */ });
  }, [get, up]);

  const onCreate = useCallback(async () => {
    const cur = get();
    setBizErr(null);
    setBizBusy(true);
    try {
      if (cur.registered) {
        await saveBusinessEdits(cur);
      } else {
        const hp = sessionStorage.getItem('mise-signup-hp') || '';
        sessionStorage.removeItem('mise-signup-hp');
        await registerAccount(cur, hp);
        up({ registered: true });
      }
      go(2);
      kickSiteImport(cur.site);
    } catch (e: unknown) {
      if (errCode(e) === 'EMAIL_EXISTS') setBizErr('That email already has an account. Sign in instead and we’ll pick this up from where you are.');
      else setBizErr(errMsg(e, 'That didn’t go through. Try again in a moment.'));
    }
    setBizBusy(false);
  }, [get, go, kickSiteImport, up]);

  // ---------- connect: sheets ----------
  const [sheet, setSheet] = useState<ToolKey | null>(null);
  const onOAuth = useCallback(async (t: ToolKey, shop?: string) => {
    const url = await startOAuth(t, shop); // throws → the sheet shows the reason
    save(get()); // progress on disk before the browser leaves
    window.location.href = url;
  }, [get]);
  const onToast = useCallback(async (creds: { clientId: string; clientSecret: string; locationGuid: string }) => {
    const locationName = await connectToast(creds);
    setSheet(null);
    startImport('toast', locationName);
  }, [startImport]);
  const onFaireToken = useCallback(async (token: string) => {
    await connectFaireToken(token);
    setSheet(null);
    startImport('faire', null);
  }, [startImport]);

  // ---------- connect: the no-POS file path ----------
  const [altErr, setAltErr] = useState<string | null>(null);
  const onAltFile = useCallback(async (f: File) => {
    setAltErr(null);
    up({ alt: 'reading' });
    try {
      const r = await importMenuFile(f);
      const cur = get();
      up({ alt: 'done', altDetail: r.detail + ', from your file', itemsImported: cur.itemsImported + r.items });
    } catch (e: unknown) {
      setAltErr(errMsg(e, 'Couldn’t read that file — a clearer photo or the original PDF usually works.'));
      up({ alt: 'open' });
    }
  }, [get, up]);

  // ---------- review: prefill from the real record, then submit ----------
  const placeFetched = useRef(false);
  useEffect(() => {
    if (!booted || s.step !== 3 || !s.registered || placeFetched.current) return;
    placeFetched.current = true;
    void fetchCurrentRestaurant().then((r) => {
      if (!r) return;
      const cur = get();
      const pl = { ...cur.place };
      let touched = false;
      const take = (k: 'street' | 'city' | 'state' | 'zip' | 'phone', v?: string | null) => { if (v && !pl[k]) { pl[k] = v; touched = true; } };
      take('street', r.addressStreet1);
      take('city', r.addressCity);
      take('state', r.addressState);
      take('zip', r.addressPostalCode);
      take('phone', r.phone);
      if (touched) { pl.src = pl.src || 'your account'; up({ place: pl }); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted, s.step, s.registered]);

  const [rvBusy, setRvBusy] = useState(false);
  const [rvErr, setRvErr] = useState<string | null>(null);
  const onReviewSubmit = useCallback(async () => {
    setRvBusy(true);
    setRvErr(null);
    try {
      const out = await submitReview(get());
      const cur = get();
      const pushed = new Set(out.pushedMenuNames);
      const invited = new Set(out.invitedEmails);
      const createdV = new Set(out.createdVendorNames);
      const vendors = cur.vendors.map((v) => (createdV.has(v.name.trim()) ? { ...v, created: true } : v));
      up({
        menu: cur.menu.map((m) => (pushed.has(m.name) ? { ...m, pushed: true } : m)),
        itemsImported: cur.itemsImported + out.pushedMenuNames.length,
        team: cur.team.map((p) => (invited.has(p.email.trim()) ? { ...p, invited: true } : p)),
        invitedCount: cur.invitedCount + out.invitedEmails.length,
        vendors,
        vendorsCreated: vendors.filter((v) => v.created).length,
      });
      go(4);
    } catch (e: unknown) {
      setRvErr(errMsg(e, 'Couldn’t save your details — check the address and try again.'));
    }
    setRvBusy(false);
  }, [get, go, up]);

  // ---------- portions → complete ----------
  const [fnBusy, setFnBusy] = useState(false);
  const [fnErr, setFnErr] = useState<string | null>(null);
  const onFinish = useCallback(async () => {
    setFnBusy(true);
    setFnErr(null);
    try {
      const cur = get();
      await finishSetup(cur.type as TypeKey, mods(cur));
      up({ completed: true });
      go(5);
    } catch (e: unknown) {
      setFnErr(errMsg(e, 'Couldn’t finish just now — try again in a moment.'));
    }
    setFnBusy(false);
  }, [get, go, up]);

  // ---------- done ----------
  const onOpenDashboard = useCallback((path = '/') => { clear(); navigate(path); }, [navigate]);
  const onSendInvites = useCallback(async () => {
    const cur = get();
    if (!cur.invite) up({ invite: true });
    const r = await sendInvites(get().team);
    const invited = new Set(r.sent);
    const now = get();
    up({
      team: now.team.map((p) => (invited.has(p.email.trim()) ? { ...p, invited: true } : p)),
      invitedCount: now.invitedCount + r.sent.length,
    });
  }, [get, up]);

  // ---------- call sheet ----------
  const [callReason, setCallReason] = useState<string | null>(null);

  if (!booted) {
    return <div className="mz-page"><div className="mz" /></div>;
  }

  const found = bizFromEmail(s.email);
  const P = {
    s, up, get, ask,
    back: () => go(s.step - 1),
    next: () => go(s.step + 1),
    onCall: (r?: string) => setCallReason(r || 'help'),
  };
  const unread = s.chat.length > 0 && s.chat[s.chat.length - 1].r === 'a';

  return (
    <div className="mz-page">
      <div ref={root} className="mz">
        <TopBar s={s} step={s.step} onHelp={() => setCallReason('help')} />
        <div className={'mz-row' + (narrow ? ' nar' : '')}>
          <div className="mz-scroll" ref={sc}>
            {s.step === 5 ? (
              <Done s={s} up={up} ask={ask} onOpenDashboard={onOpenDashboard} onSendInvites={() => void onSendInvites()} />
            ) : (
              <div className="mz-main" key={s.step}>
                {s.step === 0 ? <Account {...P} /> : null}
                {s.step === 1 ? <Business {...P} found={found && s.biz === found.name ? found.domain : null} busy={bizBusy} err={bizErr} onCreate={() => void onCreate()} /> : null}
                {s.step === 2 ? <Connect {...P} skip={() => go(3)} setSheet={setSheet} liveRows={liveRows} onAltFile={(f) => void onAltFile(f)} altErr={altErr} /> : null}
                {s.step === 3 ? <Review {...P} busy={rvBusy} err={rvErr} onSubmit={() => void onReviewSubmit()} /> : null}
                {s.step === 4 ? <Portions {...P} back={() => go(3)} busy={fnBusy} err={fnErr} onFinish={() => void onFinish()} /> : null}
              </div>
            )}
          </div>
          {narrow ? null : (
            <aside className="mz-side">
              <Assistant s={s} get={get} up={up} draft={draft} header={<Summary s={s} step={s.step} />} />
            </aside>
          )}
        </div>
        {narrow && !askOpen ? <button className={'askb' + (unread ? ' dot' : '')} onClick={() => setAskOpen(true)}>Ask Mise</button> : null}
        {narrow && askOpen ? (
          <div className="scrim" onClick={(e) => e.target === e.currentTarget && setAskOpen(false)}>
            <div className="sheet as-sheet">
              <Assistant s={s} get={get} up={up} draft={draft} onClose={() => setAskOpen(false)} header={<div className="as-top"><b>Ask Mise</b><span>Questions, or paste anything</span></div>} />
            </div>
          </div>
        ) : null}
        {sheet ? (
          <OAuthSheet
            s={s}
            t={sheet}
            close={() => setSheet(null)}
            onCall={(r) => { setSheet(null); setCallReason(r); }}
            ask={(txt, p) => { setSheet(null); ask(txt, p); }}
            onOAuth={onOAuth}
            onToast={onToast}
            onFaireToken={onFaireToken}
          />
        ) : null}
        {callReason ? <CallSheet s={s} up={up} reason={callReason} close={() => setCallReason(null)} /> : null}
      </div>
    </div>
  );
}
